import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FaShoppingCart, FaEye, FaRegHeart, FaHeart, FaWineBottle } from 'react-icons/fa';
import { addToCart } from '../../actions/cartActions';

const ProductCard = ({ product }) => {
  const dispatch = useDispatch();
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  
  const addToCartHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsAddingToCart(true);
    
    dispatch(addToCart(product.id, 1))
      .then(() => {
        setShowTooltip(true);
        setTimeout(() => {
          setShowTooltip(false);
        }, 2000);
      })
      .finally(() => {
        setTimeout(() => {
          setIsAddingToCart(false);
        }, 500);
      });
  };
  
  const toggleWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsWishlisted(!isWishlisted);
  };
  
  // Convert price to a number and handle any potential NaN cases
  const formatPrice = (price) => {
    const numPrice = parseFloat(price);
    return isNaN(numPrice) ? '0.00' : numPrice.toFixed(2);
  };
  
  return (
    <div className="product-card relative group bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 h-full flex flex-col">
      {/* Tooltip for successfully added to cart */}
      {showTooltip && (
        <div className="absolute top-2 left-0 right-0 mx-auto w-max z-10 animate-fadeInUp">
          <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm shadow-md">
            已添加到购物车
          </div>
        </div>
      )}
      
      <div className="relative overflow-hidden">
        {/* Product Tag - out of stock or discount */}
        {product.stock_quantity <= 0 ? (
          <div className="absolute top-2 left-2 z-10">
            <div className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded">
              缺货
            </div>
          </div>
        ) : product.discount ? (
          <div className="absolute top-2 left-2 z-10">
            <div className="bg-red-700 text-white text-xs font-bold px-2 py-1 rounded">
              优惠
            </div>
          </div>
        ) : null}
        
        {/* Wishlist button */}
        <button
          onClick={toggleWishlist}
          className="absolute top-2 right-2 z-10 bg-white p-2 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity transform hover:scale-110"
        >
          {isWishlisted ? (
            <FaHeart className="text-red-600" />
          ) : (
            <FaRegHeart className="text-gray-400 hover:text-red-600" />
          )}
        </button>
        
        <Link to={`/product/${product.id}`} className="block relative img-hover-zoom">
          <div className="h-56 overflow-hidden">
            {product.image_url ? (
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full h-full object-cover transition-transform duration-500"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400">
                <FaWineBottle size={48} />
              </div>
            )}
          </div>
          
          {/* Quick view button - appears on hover */}
          <div className="absolute inset-0 bg-black bg-opacity-20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
              <span className="bg-white text-gray-800 px-4 py-2 rounded-full text-sm font-medium flex items-center">
                <FaEye className="mr-2" />
                查看详情
              </span>
            </div>
          </div>
        </Link>
      </div>
      
      <div className="p-4 flex-grow flex flex-col">
        <Link to={`/product/${product.id}`}>
          <h3 className="text-lg font-semibold mb-1 hover:text-red-700 line-clamp-2">{product.name}</h3>
        </Link>
        
        {product.chinese_name && (
          <p className="text-gray-600 text-sm mb-2 line-clamp-2">{product.chinese_name}</p>
        )}
        
        {/* Product attributes */}
        <div className="flex items-center gap-4 mb-auto text-xs text-gray-500 mt-1">
          {product.alcohol_percentage && (
            <span>{product.alcohol_percentage}%</span>
          )}
          {product.volume && (
            <span>{product.volume}ml</span>
          )}
          {product.category_name && (
            <span>{product.category_name}</span>
          )}
        </div>
        
        <div className="mt-4 flex justify-between items-end">
          <div>
            <p className="text-red-700 font-bold text-xl">¥{formatPrice(product.price)}</p>
            
            {/* Show original price if there's a discount */}
            {product.original_price && (
              <p className="text-gray-500 text-xs line-through">
                ¥{formatPrice(product.original_price)}
              </p>
            )}
          </div>
          
          <button
            onClick={addToCartHandler}
            className={`flex items-center justify-center w-10 h-10 rounded-full 
              ${product.stock_quantity > 0 
                ? 'bg-red-700 text-white hover:bg-red-800' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'} 
              transition-colors relative`}
            disabled={product.stock_quantity <= 0 || isAddingToCart}
          >
            {isAddingToCart ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <FaShoppingCart />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;